import { getPageData, getPages, getLayoutData } from '../api/api';
import { getNestedPropSafely, getPropSafely } from '@repo/utils';
import { FRAGMENTS } from '@repo/ui/lib';
import { DIGITAL_FRAGMENTS } from '@/lib';
import ExtendedContent from '@/components/common/ExtendedContent';
import TagsBanner from '@repo/ui/src/components/Banners/TagsBanner';
import dynamic from 'next/dynamic';

const StickyHeading = dynamic(() => import('@repo/ui/src/components/StickyHeading'), {
	ssr: false,
});

export default function Page({ data, locale }) {
	const contentData = getPropSafely(data, 'content', []);
	const services = getPropSafely(data, 'services', []);
	const stickyHeadingsSelectors = getPropSafely(data, 'stickyHeadingsSelector', []);

	const bannerData = {
		header: getNestedPropSafely(data, ['banner', 'header'], ['text'], ''),
		backButton: getPropSafely(data, 'backButtonText', ''),
		backToAllButton: getPropSafely(data, 'backToAllButtonText', ''),
		backLink: '/competences',
		card: getPropSafely(data, 'card', {}),
		borderColor: '#D2D2D2',
	};

	const tags = {
		header: locale === 'pl' ? 'Usługi:' : 'Services:',
		data: services,
		prefix: 'services/',
	};

	return (
		<>
			<StickyHeading stickyHeadingsSelectors={stickyHeadingsSelectors} />
			<TagsBanner data={bannerData} tags={tags} />
			<ExtendedContent content={contentData} />
		</>
	);
}

const PAGE_CONTENT_TYPE = 'allCommonCompetencesPages';
const PLEO_TYPE = 'digital';

const pathsQuery = `
query GetSingleCompetence($locale: SiteLocale) {
	${PAGE_CONTENT_TYPE}(locale: $locale, filter: {pleoType: {eq: "${PLEO_TYPE}"}}, first: 100) {
       competenceName
       pleoType
       slug
	}
}
`;

const propsQuery = `
	query GetCompetence($slug: String, $locale: SiteLocale) {
        ${PAGE_CONTENT_TYPE}(locale: $locale, filter: { slug: { eq: $slug }}) {
            competenceName
            slug
            ${FRAGMENTS.SEO_META_FRAGMENT}
            banner{
              header{
                text
              }
            }
            backButtonText
            backToAllButtonText
            card {
              icon {
                ${FRAGMENTS.IMAGE_FRAGMENT}
              }
            }
            services: _allReferencingCommonServicePages{
              id
              slug
              name: serviceName
            }
            
            ${DIGITAL_FRAGMENTS.STICKY_HEADING_FRAGMENT}
            
            content{
                __typename
               
                ${FRAGMENTS.PRODUCTS_GRID_FRAGMENT}
                ${FRAGMENTS.CHECK_OTHER_SERVICES_FRAGMENT} 
				${FRAGMENTS.TEXT_MODULE_FRAGMENT}
				${FRAGMENTS.TECHNOLOGIES_TILES_FRAGMENT}
                ${FRAGMENTS.BENEFITS_MODULE_FRAGMENT}
                ${FRAGMENTS.CLIENTS_ABOUT_US_RECORD_FRAGMENT}
                ${FRAGMENTS.NUMBERS_RECORD_FRAGMENT}
                
                ${DIGITAL_FRAGMENTS.TILES_MODULE_FRAGMENT}
                ${DIGITAL_FRAGMENTS.SINGLE_ASSET_FRAGMENT}
                ${DIGITAL_FRAGMENTS.FAQ_FRAGMENT}
				${DIGITAL_FRAGMENTS.CHECK_OTHERS_FRAGMENT}
				${DIGITAL_FRAGMENTS.PROJECTS_GRID_FRAGMENT}
            }
        }
	}
`;

export async function getStaticProps({ params, locale }) {
	const { slug } = params;
	const data = await getPageData(PAGE_CONTENT_TYPE, slug, propsQuery, locale);
	const { globals, cookies, menu, footer } = await getLayoutData(locale);
	return {
		props: {
			data,
			globals,
			cookies,
			locale,
			menu,
			footer,
		},
		revalidate: 120,
	};
}

export async function getStaticPaths({ locales }) {
	const paths = [];
	for (const locale of locales) {
		const projectPosts = await getPages(PAGE_CONTENT_TYPE, pathsQuery, locale);
		const posts = projectPosts.map((page) => ({
			params: {
				slug: page.slug,
			},
			locale,
		}));
		paths.push(...posts);
	}

	return {
		paths,
		fallback: false,
	};
}
